import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
// Rxjs
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
// Environment
import { environment } from 'src/environments/environment';
// Model
import { MessageType } from '../components/messages/message-handler/message-handler.component';
import { MessageHandlerService } from '../components/messages/message-handler/message-handler.service';
import { SaveType } from '../core/models/enumerations/save-type';
import { PlantSearch, PlantSearchAdapter } from '../core/models/index';
// Interface
import { TranslateService } from '@ngx-translate/core';
import { Cacheable, LocalStorageStrategy } from 'ts-cacheable';
import { LoadWheelService } from '../components/load-wheel/load-wheel.service';
import { BoilersDisplay } from '../core/models/boiler/display/boilers-display';
import { BoilerType, News, RelatedEntityResult } from '../core/models/common';
import { EntityName } from '../core/models/enumerations/entity-name';
import { BoilerSearch, BoilerSearchAdapter } from '../core/models/search/boiler-search';
import { SpinnerProcess } from '../core/models/spinner-process';
import { RecordLockingFlow } from '../core/record-locking/record-locking-flow';
import { EntityCommonService } from '../core/services/entity-common.service';
import { LockSameUserMessageService, LockSameUserResult } from '../core/services/lock-same-user-message.service';
import { TelemetryService } from '../core/services/telemetry.service';
import { EntityUtilsService } from '../core/utils/entity-utils.service';

@Injectable({
    providedIn: 'root'
})
export class BoilerSectionService {

    constructor(
        private readonly boilerSearchAdapter: BoilerSearchAdapter,
        private readonly plantAdapter: PlantSearchAdapter,
        private readonly recordLockingFlow: RecordLockingFlow,
        private readonly http: HttpClient,
        private readonly messageHandlerService: MessageHandlerService,
        private readonly loadWheelService: LoadWheelService,
        private readonly entityCommonService: EntityCommonService,
        private readonly translate: TranslateService,
        private readonly entityUtilsService: EntityUtilsService,
        private readonly lockSameUserMessageService: LockSameUserMessageService,
        private readonly telemetry: TelemetryService,
    ) {}

    getBoilerDetailsById(boilerId: string, isReload: boolean = false, quickAccess: boolean = false): Promise<BoilersDisplay> {
        let mainLockID: number = 0;

        if (isReload) {
            mainLockID = this.recordLockingFlow.getLockID(EntityName.BOILER, boilerId);
        }

        return new Promise<BoilersDisplay>((resolve) => {
            this.lockSameUserMessageService.validateEntityIsLockedByUser(this.recordLockingFlow, EntityName.BOILER, boilerId, mainLockID, quickAccess).then((lockSameUserResult: LockSameUserResult) => {
                if (lockSameUserResult.openEntity) {
                    this.getEntityDetailsById(boilerId, mainLockID, false).subscribe((entityDisplay: BoilersDisplay) => {
                        resolve(entityDisplay);
                    });
                } else {
                    resolve(BoilersDisplay.BuildEntityDisplay({}));
                }
            });
        });
    }

    private getEntityDetailsById(boilerId: string, mainLockID: number, onlyForRead: boolean): Observable<BoilersDisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.boiler") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<BoilersDisplay>(`${environment.apiUrl}boiler/${boilerId}/${mainLockID}/${onlyForRead}`)
            .pipe(
                map((data: any) => {
                    let boilerDisplay = BoilersDisplay.BuildEntityDisplay({});
                    if (data.response) {
                        boilerDisplay = BoilersDisplay.BuildEntityDisplay(data.response);

                        if (boilerDisplay.boilerId) {
                            if (boilerDisplay.hasDiffUserLockError()) {
                                this.messageHandlerService.show(boilerDisplay.lockError, MessageType.INFO);
                            }

                            // Set the locking info into lock global list.
                            this.recordLockingFlow.setLockItem(EntityName.BOILER, boilerDisplay.boilerId, boilerDisplay.lockId, boilerDisplay.lockMode);
                            this.recordLockingFlow.initLockRefreshTimer(EntityName.BOILER, boilerDisplay.boilerId, boilerDisplay.lockId);

                        } else {
                            this.messageHandlerService.show(boilerDisplay.lockError, MessageType.ERROR);
                        }
                    } else {
                        const errorType = MessageHandlerService.errorType(data);
                        if (errorType === MessageType.INFO) {
                            this.entityCommonService.sendEntityNotFoundEvent();
                        } else {
                            this.messageHandlerService.show(MessageHandlerService.errorMessage(data), errorType);
                        }
                    }
                    return boilerDisplay;
                }),
                catchError((error) => {
                    return of(BoilersDisplay.BuildEntityDisplay({}));
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getBoilerById(boilerId: number): Observable<BoilerSearch> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.boiler") + ' (' + this.translate.instant("loading.details") + ')');
        return this.http.get<BoilerSearch>(`${environment.apiUrl}boiler/${boilerId}`)
            .pipe(
                map((data: any) => {
                    return this.boilerSearchAdapter.adapt(data.response[0]);
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getPlantById(plantId: number): Observable<PlantSearch> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.plant"));
        return this.http.get<PlantSearch>(`${environment.apiUrl}plant/${plantId}`)
            .pipe(
                map((data: any) => {
                    return this.plantAdapter.adapt(data.response[0]);
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    saveBoilerDetails(boilerDisplay: BoilersDisplay): Observable<BoilersDisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("saving.equipment"));
        boilerDisplay = this.entityUtilsService.prepareEntityForSave(boilerDisplay) as BoilersDisplay;
        return this.http.put<BoilersDisplay>(`${environment.apiUrl}boiler`, boilerDisplay)
            .pipe(
                map((data: any) => {
                    if (data.response === null) {
                        if (data.exception !== null) {
                            this.messageHandlerService.showExceptionDialog(data);
                        } else if (data.message !== null) {
                            if (boilerDisplay.saveType === SaveType.TYPE_QC || boilerDisplay.saveType === SaveType.UPDATE) {
                                //When the user attempts to QC a record while the lock was already cleared,
                                //this//the above warning message should still be thrown; however, the record should not reload.
                                if (data.messageType !== MessageType.WARNING.valueOf()) {
                                    this.entityCommonService.sendReloadEvent();
                                }
                            } else {
                                boilerDisplay.intDataDepMigError = data.message;
                            }

                        }
                    } else {
                        const isQcUpdateOrAmend = boilerDisplay.saveType === SaveType.TYPE_QC ||
                            boilerDisplay.saveType === SaveType.AMENDMENT ||
                            boilerDisplay.saveType === SaveType.UPDATE;

                        const afterClearFunc = () => this.entityCommonService.sendPreReleaseEvent();
                        this.recordLockingFlow.clearLockFlow(EntityName.BOILER, String(boilerDisplay.boilerId), isQcUpdateOrAmend, afterClearFunc);
                    }
                    return data.response;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    boilerNextId(): Observable<BoilersDisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.nextId"));
        return this.http.get<any>(`${environment.apiUrl}boiler/nextId`)
            .pipe(
                map((data: any) => {
                    const boilerDisplay = BoilersDisplay.BuildNewEntity(data.response);
                    if (boilerDisplay?.boilerId) {
                        // Set the locking info into lock global list.
                        this.recordLockingFlow.setLockItem(EntityName.BOILER, boilerDisplay.boilerId, -1);
                    }
                    return boilerDisplay;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    searchBoiler(httpParams: HttpParams): Observable<BoilerSearch[]> {
        const searchStart: number = performance.now();

        return this.http.get<BoilerSearch[]>(`${environment.apiUrl}boiler/search`, { params: httpParams })
            .pipe(
                map((data: any) => {
                    let boilers: Array<BoilerSearch> = data.response;
                    this.entityUtilsService.verifyMaxResult(data);
                    this.telemetry.entitySearchTime("boiler", performance.now() - searchStart);
                    return boilers.map(item => this.boilerSearchAdapter.adapt(item));
                })
            );
    }

    @Cacheable({
        storageStrategy: LocalStorageStrategy
    })
    getBoilerTypes(): Observable<BoilerType[]> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("boiler.searchBoiler.boilerType"));
        return this.http.get<BoilerType>(`${environment.apiUrl}boiler/boilerSubtypes`)
            .pipe(
                map((data: any) => BoilerType.BuildBoilerType(data.response)),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getNewsInfo(boilerId: number): Observable<RelatedEntityResult<News>> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("relatedInfo.common.related") + ' ' + this.translate.instant("relatedInfo.news.title"));
        return this.http.get<RelatedEntityResult<News>>(`${environment.apiUrl}boiler/news/${boilerId}`)
            .pipe(
                map((data: any) => RelatedEntityResult.Build(data.response, News.BuildNew)),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

}


